import { isEmpty } from 'lodash';
import { requestPrices } from '../../api/travelCalcApi';
import { TRAVEL_PLANS, TRAVEL_PLANS_LABEL } from '../../constants/travelCalcConstants';
import {
  getOnlyLiteDisallowedCountry,
  getQuoteRequests,
} from '../../helpers/travel/travelCalcHelpers';
import { payloadSchema } from '../../validations/travelCalcValidations';
import {
  getDiffInYearsFromNow,
  getEarliestDateFromArray,
  getArmenianStartDate,
} from '../../helpers/dates';
import { ACTION_NAME } from '../../constants';
import { createReducerWithName } from '../middlewares/createReducerWithName';

export const SET_AGE = 'efes/travelCalc/SET_AGE';
export const SET_REGION = 'efes/travelCalc/SET_REGION';
export const SELECT_DESTINATIONS = 'efes/travelCalc/SET_DESTINATIONS';
export const SET_RANGE = 'efes/travelCalc/SET_RANGE';
export const SET_DAYS = 'efes/travelCalc/SET_DAYS';
export const SELECT_PACKAGE = 'efes/travelCalc/SELECT_PACKAGE';
export const SELECT_LIMIT = 'efes/travelCalc/SELECT_LIMIT';
export const SELECT_PLAN = 'efes/travelCalc/SELECT_PLAN';
export const SELECT_COVERAGES = 'efes/travelCalc/SELECT_COVERAGES';
export const CLEAR_COVERAGES = 'efes/travelCalc/CLEAR_COVERAGES';
export const GET_PRICES = 'efes/travelCalc/GET_PRICES';
export const GET_PRICES_SUCCESS = 'efes/travelCalc/GET_PRICES_SUCCESS';
export const GET_PRICES_FAIL = 'efes/travelCalc/GET_PRICES_FAIL';
export const SET_PRICES_WITH_PROMO_CODE = 'efes/travelCalc/SET_PRICES_WITH_PROMO_CODE';
export const CLEAR_CALCULATION = 'efes/travelCalc/CLEAR_CALCULATION';
export const CLEAR_PRICES = 'efes/travelCalc/CLEAR_PRICES';
export const CLEAR_PLAN_AND_PRICES = 'efes/travelCalc/CLEAR_PLAN_AND_PRICES';
export const SET_MOBILE_CONTRACT_DATA = 'efes/travelCalc/SET_MOBILE_CONTRACT_DATA';
export const SET_MOBILE_CONTRACT_DATA_SUCCESS = 'efes/travelCalc/SET_MOBILE_CONTRACT_DATA_SUCCESS';
export const SET_MOBILE_CONTRACT_DATA_FAIL = 'efes/travelCalc/SET_MOBILE_CONTRACT_DATA_FAIL';

const initialState = {
  age: 0,
  region: '',
  selectedDestinations: [],
  range: {
    start: getArmenianStartDate(),
  },
  days: 0,
  selectedPackage: {},
  selectedLimit: {},
  selectedPlan: '',
  selectedCoverages: {},
  prices: {},
  policyPrices: {},
  isFetchingPrice: false,
  fetchPricesError: null,
  loadingMobileContractData: false,
};

const shapePrices = (prices) => prices.reduce((acc, price) => {
  const prop = price.package;
  const value = price.price;
  return { ...acc, [prop]: value };
}, {});

const setPlanTypeToKey = (prices) => prices.reduce((acc, price) => {
  const prop = price.package;
  return { ...acc, [prop]: price };
}, {});

function reducer(state = initialState, action = {}) {
  switch (action.type) {
    case SET_AGE:
      return {
        ...state,
        age: action.data,
      };
    case SET_REGION:
      return {
        ...state,
        region: action.data,
      };
    case SELECT_DESTINATIONS:
      return {
        ...state,
        selectedDestinations: action.data,
      };
    case SET_RANGE:
      return {
        ...state,
        range: { ...state.range, ...action.data },
      };
    case SET_DAYS:
      return {
        ...state,
        days: action.data,
      };
    case SELECT_PACKAGE:
      return {
        ...state,
        selectedPackage: action.data,
      };
    case SELECT_LIMIT:
      return {
        ...state,
        selectedLimit: action.data,
      };
    case SELECT_PLAN:
      return {
        ...state,
        selectedPlan: action.data,
      };
    case SELECT_COVERAGES:
      return {
        ...state,
        selectedCoverages: action.data,
      };
    case CLEAR_COVERAGES:
      return {
        ...state,
        selectedCoverages: {},
      };
    case GET_PRICES:
      return {
        ...state,
        prices: {},
        isFetchingPrice: true,
      };
    case GET_PRICES_SUCCESS:
      return {
        ...state,
        isFetchingPrice: false,
        policyPrices: setPlanTypeToKey(action.result),
        prices: shapePrices(action.result),
      };
    case GET_PRICES_FAIL:
      return {
        ...state,
        isFetchingPrice: false,
        fetchPricesError: action.error,
      };
    case CLEAR_PLAN_AND_PRICES:
      return {
        ...state,
        selectedPlan: '',
        prices: {},
      };
    case CLEAR_PRICES:
      return {
        ...state,
        prices: {},
        policyPrices: {},
      };
    case SET_PRICES_WITH_PROMO_CODE:
      return {
        ...state,
        policyPrices: action.policyPrices,
      };
    case SET_MOBILE_CONTRACT_DATA:
      return {
        ...state,
        loadingMobileContractData: true,
      };
    case SET_MOBILE_CONTRACT_DATA_SUCCESS:
      return {
        ...state,
        ...(action.result && action.result),
        loadingMobileContractData: false,
      };
    case SET_MOBILE_CONTRACT_DATA_FAIL:
      return {
        ...state,
        loadingMobileContractData: false,
      };
    case CLEAR_CALCULATION:
      return initialState;
    default:
      return state;
  }
}

export const travelCalculationReducerForContract = createReducerWithName('contract', reducer, initialState);
const travelCalculationReducerForCalculatorPage = createReducerWithName('calculator', reducer, initialState);

export { travelCalculationReducerForCalculatorPage as default };

export const setAge = (age, name) => ({
  type: SET_AGE,
  data: age,
  name,
});

export const setRegion = (region, name) => ({
  type: SET_REGION,
  data: region,
  name,
});

export const selectDestinations = (destinations, name) => ({
  type: SELECT_DESTINATIONS,
  data: destinations,
  name,
});

export const setRange = (range, name) => ({
  type: SET_RANGE,
  data: range,
  name,
});

export const setDays = (days, name) => ({
  type: SET_DAYS,
  data: days,
  name,
});

export const selectPackage = (pack, name) => ({
  type: SELECT_PACKAGE,
  data: pack,
  name,
});

export const selectLimit = (limit, name) => ({
  type: SELECT_LIMIT,
  data: limit,
  name,
});

export const selectPlan = (plan, name) => ({
  type: SELECT_PLAN,
  data: plan,
  name,
});

export const selectCoverages = (coverages, name) => ({
  type: SELECT_COVERAGES,
  data: coverages,
  name,
});

export const clearCoverages = (name) => ({
  type: CLEAR_COVERAGES,
  name,
});

export const setPricesWithPromoCode = (policyPrices) => (dispatch) => {
  dispatch({
    type: SET_PRICES_WITH_PROMO_CODE,
    policyPrices,
  });
};

export const clearCalculation = (name) => ({
  type: CLEAR_CALCULATION,
  name,
});

export const clearPrices = (name) => ({
  type: CLEAR_PRICES,
  name,
});

export const clearPlanAndPrices = (name) => ({
  type: CLEAR_PLAN_AND_PRICES,
  name,
});

export const getPrices = (data, name) => (dispatch) => {
  const {
    travelers,
    age,
    region,
    days,
    selectedLimit,
    selectedDestinations,
    selectedCoverages,
    promoCodes,
    range,
    selectedPlan,
  } = data;

  if (!region || !days || !selectedLimit) {
    return;
  }

  if (isEmpty(selectedLimit)) {
    return;
  }

  if (range && (!range.start || !range.end)) {
    return;
  }

  let plans;
  let selectedAge;
  let travelersData;

  const hasOnlyLiteDisallowedCountry = getOnlyLiteDisallowedCountry(selectedDestinations);
  if (hasOnlyLiteDisallowedCountry) {
    plans = Object.values(TRAVEL_PLANS).filter((plan) => plan.id !== TRAVEL_PLANS_LABEL.LITE);
  } else {
    plans = Object.values(TRAVEL_PLANS).filter((plan) => plan.id !== TRAVEL_PLANS_LABEL.GEORGIA);
  }

  if (name === ACTION_NAME.CONTRACT) {
    travelersData = travelers && travelers.length
      && travelers.map((traveler) => ({
        birthday: traveler.birthday,
        passport: traveler.passport,
        socCard: traveler.socCard || '', // We pass empty string to {socCard}, Masys throws exception when {socCard} isn't exists
        promoCode: traveler.promoCode || '',
        age: getDiffInYearsFromNow(traveler.birthday),
      }));
    if (!travelersData || !travelersData.length) {
      return;
    }

    const birthdays = travelersData.map((el) => el.birthday);
    const validBirthdays = birthdays.filter((date) => !!date);
    const earliestBirthday = getEarliestDateFromArray(validBirthdays) || null;
    selectedAge = earliestBirthday ? getDiffInYearsFromNow(earliestBirthday) : 0;
  } else {
    if (!age) {
      return;
    }
    travelersData = [{ age, passport: null, socCard: null, promoCode: null }];
    selectedAge = age;
  }

  const quoteRequests = getQuoteRequests(
    plans,
    selectedLimit,
    selectedCoverages,
    selectedAge,
    selectedPlan,
  );

  const payload = {
    travelers: travelersData,
    region,
    days,
    quoteRequests,
    promoCodes,
    travelPolicyStart: range && range.start,
    travelPolicyEnd: range && range.end,
    hasOnlyLiteDisallowedCountry: selectedPlan === TRAVEL_PLANS_LABEL.GEORGIA
      ? hasOnlyLiteDisallowedCountry
      : false,
  };

  payloadSchema.isValid(payload).then((isValid) => {
    if (isValid) {
      dispatch({
        actions: [
          { type: GET_PRICES, name },
          { type: GET_PRICES_SUCCESS, name },
          {
            type: GET_PRICES_FAIL,
            name,
            showError: true,
            customMsg: 'getPricesFail',
          },
        ],
        promise: requestPrices(payload),
      });
    }
  });
};
